/* eslint-disable no-await-in-loop, no-console */
import debugModule from 'debug'
import Config from '../config'
import {
  scanLogFiles, flushLogToFile, serializeLogEntry as serializeLogEntryLocal, _resetLogHandle,
} from './local'
import { serializeLogEntry as serializeLogEntryRemote, flushLogToRemote, isRemoteSupported } from './remote'
import { jsonStringify } from './helpers'

debugModule.useColors = () => false
debugModule.disable()
const logger = debugModule('aha')

const bufferLengthBeforeFlush = 20
const logToConsole = ['staging', 'develop', 'local'].includes(Config.env)
const logConfig = {
  useRemote: false,
  context: undefined,
  bufferSize: bufferLengthBeforeFlush,
}

const logBuffer = []
// A simple flag to prevent concurrent executions
let isFlushing = false

const flushLog = async () => {
  // 1. Prevent overlapping calls and check if there's work to do
  if (isFlushing || logBuffer.length === 0) return

  isFlushing = true

  // 2. Capture and clear the buffer atomically
  const linesToFlush = [...logBuffer]
  logBuffer.length = 0

  try {
    if (logConfig.useRemote) {
      await flushLogToRemote(linesToFlush)
    } else {
      await flushLogToFile(linesToFlush)
    }
  } finally {
    isFlushing = false
  }
}

// Write message to log file
/**
 * @param  {...any} args
 * @returns {Promise<boolean>}
 */
const writeLog = async (...args) => {
  try {
    // Format arguments like console.log does
    const message = args.filter((arg) => !!arg).map((arg) => {
      if (typeof arg === 'object') {
        try {
          return jsonStringify(arg)
        } catch (err) {
          return `stringifyError - ${err.message}`
        }
      }
      return String(arg)
    }).join(' ')
    const additionalContext = args?.[2]
    const logEntry = logConfig?.useRemote
      ? serializeLogEntryRemote(message, additionalContext ? { ...logConfig.context, ...additionalContext } : logConfig.context)
      : serializeLogEntryLocal(message)
    logBuffer.push(logEntry)

    if (logBuffer.length >= logConfig.bufferSize) {
      await flushLog()
    }

    return true
  } catch (error) {
    console.error('Failed to write to log file:', error)
    return false
  }
}

const onVisibilityChange = () => {
  if (document.visibilityState === 'hidden') {
    flushLog().catch((error) => {
      console.error('Failed to flush log on visibility change:', error)
    })
  }
}

// Setup cleanup event listeners
const setupCleanup = () => {
  // Handle tab/window close using visibilitychange event
  document.addEventListener('visibilitychange', onVisibilityChange)
}

// Assign writeLog to debug.log for compatibility
logger.log = logToConsole ? (...args) => {
  const hasError = args.some((arg) => arg instanceof Error)
  if (hasError) {
    console.error(...args)
  } else {
    console.log(...args)
  }
  writeLog(...args)
} : (...args) => writeLog(...args)

const enableLog = ({ useRemote = false, bufferSize = bufferLengthBeforeFlush } = {}) => {
  debugModule.enable('aha*')
  if (isRemoteSupported()) {
    logConfig.useRemote = useRemote
  }
  if (bufferSize && Number.isFinite(bufferSize)) {
    logConfig.bufferSize = bufferSize
  }
  // Initialize cleanup on module load
  setupCleanup()
}

const disableLog = () => {
  debugModule.disable()
  document.removeEventListener('visibilitychange', onVisibilityChange)
}
const setLogContext = (context = {}) => {
  logConfig.context = logConfig.context || {}
  Object.assign(logConfig.context, context)
}

const _resetLogger = () => {
  logBuffer.length = 0
  isFlushing = false
  _resetLogHandle()
}
export {
  enableLog,
  disableLog,
  writeLog,
  bufferLengthBeforeFlush,
  scanLogFiles,
  flushLog,
  setLogContext,
  _resetLogger,
}
export default logger
